import { useEffect } from 'react';
import { analytics } from './analytics';
import { AnalyticsEvent, AnalyticsSessionTrigger } from './analyticsEvent';
import { session } from './sessionState';

// A tab hidden for at least this long starts a new session when it becomes visible again.
export const SESSION_RETURN_THRESHOLD_MS = 30 * 60 * 1000;

const localDayKey = (ms: number) => {
  const date = new Date(ms);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const start = (trigger: AnalyticsSessionTrigger) => {
  session.lastStartedAt = Date.now();
  // A session belongs to the organization, not to whichever space was open last.
  analytics.track(AnalyticsEvent.sessionStarted, {
    trigger,
    spaceId: null,
    organizationId: session.organizationId,
  });
};

const sendLoad = (userId: string) => {
  session.loadedUserId = userId;
  session.pendingLoadUserId = null;
  session.hiddenAt = null;
  start('load');
};

const isNewDay = () => session.lastStartedAt !== null && localDayKey(Date.now()) !== localDayKey(session.lastStartedAt);

/**
 * Sends app.session.started:
 * - `load` once per app load and user, once the user is identified and the active organization
 *   is known. A tab opened in the background sends it when first viewed instead;
 * - `return` when the tab becomes visible after being hidden for SESSION_RETURN_THRESHOLD_MS;
 * - `new_day` on the first interaction (or visibility return) on a local calendar day other
 *   than the one of the last session.started sent from this page.
 * Nothing is sent before `load`. Mount after the effect that identifies the user.
 */
export const useSessionTracking = ({
  userId,
  organizationId,
}: {
  userId?: number | string;
  organizationId?: number;
}) => {
  useEffect(() => {
    if (organizationId !== undefined) session.organizationId = organizationId;
    if (userId === undefined || organizationId === undefined) return;
    const id = String(userId);
    if (session.loadedUserId === id) return;
    if (document.hidden) {
      session.pendingLoadUserId = id;
      return;
    }
    sendLoad(id);
  }, [userId, organizationId]);

  useEffect(() => {
    if (document.hidden && session.hiddenAt === null) session.hiddenAt = Date.now();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        session.hiddenAt = Date.now();
        return;
      }
      const hiddenAt = session.hiddenAt;
      session.hiddenAt = null;
      if (session.pendingLoadUserId !== null) {
        // First view of a background tab: this is the app load, not a return.
        sendLoad(session.pendingLoadUserId);
        return;
      }
      if (session.lastStartedAt === null) return;
      if (hiddenAt !== null && Date.now() - hiddenAt >= SESSION_RETURN_THRESHOLD_MS) {
        start('return');
      } else if (isNewDay()) {
        start('new_day');
      }
    };
    const handleInteraction = () => {
      if (isNewDay()) start('new_day');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pointerdown', handleInteraction, { capture: true, passive: true });
    window.addEventListener('keydown', handleInteraction, { capture: true, passive: true });
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pointerdown', handleInteraction, { capture: true });
      window.removeEventListener('keydown', handleInteraction, { capture: true });
    };
  }, []);
};
